import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, X } from 'lucide-react';
import Accordion from '../../../shared/Accordion';
import { GoogleLookupPill } from '../../../shared/Pill';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import SaveButton from '../../../shared/SaveButton';
import CancelButton from '../../../shared/CancelButton';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { formatDate } from '../../../shared/tenantClock';
import { askConfirm } from '../../../shared/AppDialogs';

// Mon-Sun, matching the backend's ISO-8601 day_of_week (1=Monday...7=Sunday)
// — see commercialEntityOpeningHours.js.
const DAYS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
];

function formatTime(hhmmss) {
  if (!hhmmss) return '';
  const [h, m] = hhmmss.split(':').map(Number);
  const period = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${period}` : `${h12}:${String(m).padStart(2, '0')}${period}`;
}

function rangesByDay(ranges) {
  const byDay = {};
  DAYS.forEach((d) => { byDay[d.value] = []; });
  (ranges || []).forEach((r) => { if (byDay[r.day_of_week]) byDay[r.day_of_week].push(r); });
  return byDay;
}

// A blank range field group defaults to 9am-5pm rather than empty — every
// "+ Add range" click and a freshly-opened empty week both start from a
// plausible business-hours guess instead of a pair of blank time pickers.
function newRange() {
  return { key: Math.random().toString(36).slice(2), open_time: '09:00', close_time: '17:00' };
}

// Opening Hours card (issue #203) — Google Places auto-lookup first, manual
// entry as the fallback/degrade path. Display mode shows the week + Last
// Checked/staleness; Edit mode is a self-contained Save/Cancel form (the
// day x range grid doesn't fit InlineEditField's one-scalar-per-field
// shape used everywhere else on this page).
function OpeningHoursCard({ entityId, entity, onUpdate }) {
  const { canUpdate } = useScreenAccess();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [editing, setEditing] = useState(false);
  const [byAppointment, setByAppointment] = useState(false);
  const [notes, setNotes] = useState('');
  const [draftRanges, setDraftRanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupMessage, setLookupMessage] = useState('');
  const [undoing, setUndoing] = useState(false);

  const startEdit = () => {
    setByAppointment(!!entity.opening_hours_by_appointment);
    setNotes(entity.opening_hours_notes || '');
    const byDay = rangesByDay(entity.opening_hours);
    const draft = {};
    DAYS.forEach((d) => {
      draft[d.value] = byDay[d.value].length
        ? byDay[d.value].map((r) => ({ key: `${r.id}`, open_time: r.open_time.slice(0, 5), close_time: r.close_time.slice(0, 5) }))
        : [];
    });
    setDraftRanges(draft);
    setLookupMessage('');
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const addRange = (day) => setDraftRanges((prev) => ({ ...prev, [day]: [...prev[day], newRange()] }));
  const removeRange = (day, key) => setDraftRanges((prev) => ({ ...prev, [day]: prev[day].filter((r) => r.key !== key) }));
  const updateRange = (day, key, field, value) => setDraftRanges((prev) => ({
    ...prev,
    [day]: prev[day].map((r) => (r.key === key ? { ...r, [field]: value } : r)),
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const ranges = DAYS.flatMap((d) => draftRanges[d.value].map((r) => ({
        day_of_week: d.value,
        open_time: `${r.open_time}:00`,
        close_time: `${r.close_time}:00`,
      })));
      const res = await axios.put(`/api/commercial-entities/${entityId}/opening-hours`, {
        by_appointment: byAppointment,
        notes,
        ranges,
      });
      onUpdate(res.data.data);
      setEditing(false);
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };

  const handleLookup = async () => {
    if (lookingUp) return;
    setLookingUp(true);
    setLookupMessage('');
    try {
      const res = await axios.post(`/api/commercial-entities/${entityId}/opening-hours/lookup`);
      onUpdate(res.data.data);
      setLookupMessage(res.data.message || '');
      if (!res.data.found) setTimeout(startEdit, 0);
      dismissError();
    } catch (err) {
      showError(err, 'Look up failed');
    }
    setLookingUp(false);
  };

  // Undoes a Google overwrite of manually-entered hours (single level —
  // see replaceOpeningHoursRanges's own comment). Only ever shown when
  // there's actually a snapshot to restore.
  const handleUndo = async () => {
    setUndoing(true);
    try {
      const res = await axios.post(`/api/commercial-entities/${entityId}/opening-hours/undo`);
      onUpdate(res.data.data);
      setLookupMessage('');
      dismissError();
    } catch (err) {
      showError(err, 'Restore failed');
    }
    setUndoing(false);
  };

  // Kebab "Reset" — clears Hours completely and removes the Primary
  // Address, back to exactly the never-set state (backend note: doesn't
  // undo a phone/website the same lookup may have filled in — see
  // resetOpeningHoursAndAddress's own comment). Destructive/hard to reverse
  // (no undo of its own, unlike the single-level snapshot above), so it's
  // gated behind a confirm rather than a single click.
  const handleReset = async () => {
    if (!(await askConfirm('Reset Opening Hours and the Primary Address? This clears all entered/looked-up hours and removes the Primary Address. Other addresses on this record are untouched.', { confirmLabel: 'Reset', destructive: true }))) return;
    try {
      const res = await axios.post(`/api/commercial-entities/${entityId}/opening-hours/reset`);
      onUpdate(res.data.data);
      setLookupMessage('');
      setEditing(false);
      dismissError();
    } catch (err) {
      showError(err, 'Reset failed');
    }
  };

  const headerExtra = canUpdate && (
    <>
      <GoogleLookupPill
        stale={entity.opening_hours_stale}
        noMatch={entity.opening_hours_google_no_match}
        untried={!entity.opening_hours_checked_at}
        staleLabel="Hours Stale"
        checking={lookingUp}
        onClick={handleLookup}
      />
      {entity.opening_hours_previous_manual && (
        <span
          onClick={undoing ? undefined : handleUndo}
          title={undoing ? 'Restoring…' : 'Overwritten by Google — click to restore your manually-entered hours'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            backgroundColor: '#2563eb',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1,
            cursor: undoing ? 'default' : 'pointer',
            opacity: undoing ? 0.6 : 1,
          }}
        >
          ↺
        </span>
      )}
    </>
  );

  return (
    <Accordion
      title="Opening Hours"
      headerExtra={headerExtra}
      defaultOpen={false}
      menuItems={[{ key: 'reset', label: 'Reset', danger: true, needs: 'update', onClick: handleReset }]}
    >
      {!editing ? (
        <div>
          {entity.opening_hours_by_appointment ? (
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>By appointment only</div>
          ) : !entity.opening_hours_checked_at ? (
            // checked_at is only ever null before the first save (manual or
            // Google) — replaceOpeningHoursRanges stamps it every time
            // ranges are written, closed days included. So a null here
            // means nothing has been entered at all, not "closed every
            // day" — showing the per-day "Closed" list would claim
            // knowledge this record doesn't have yet.
            <div style={{ fontSize: 13, color: '#aab2bd', marginBottom: 8 }}>Hours haven't been checked yet</div>
          ) : (
            <div style={{ marginBottom: 8 }}>
              {DAYS.map((d) => {
                const dayRanges = (entity.opening_hours || []).filter((r) => r.day_of_week === d.value);
                return (
                  <div key={d.value} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', borderBottom: '1px solid #f0f2f5' }}>
                    <span style={{ color: '#5a6b85' }}>{d.label}</span>
                    <span style={{ color: dayRanges.length ? '#222' : '#aab2bd' }}>
                      {dayRanges.length ? dayRanges.map((r) => `${formatTime(r.open_time)}–${formatTime(r.close_time)}`).join(', ') : 'Closed'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {entity.opening_hours_notes && (
            <div style={{ fontSize: 13, color: '#666', fontStyle: 'italic', marginBottom: 8 }}>{entity.opening_hours_notes}</div>
          )}
          <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
            {entity.opening_hours_checked_at
              ? `Last checked ${formatDate(entity.opening_hours_checked_at)} (${entity.opening_hours_source === 'google' ? 'via Google Places' : 'manual'})`
              : 'Never checked'}
          </div>
          {lookupMessage && <div style={{ fontSize: 12, color: '#b45309', marginBottom: 8 }}>{lookupMessage}</div>}
          {canUpdate && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={startEdit}
              style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0 }}
            >
              Edit
            </button>
          </div>
          )}
        </div>
      ) : (
        <div>
          <ToggleSwitch checked={byAppointment} onChange={setByAppointment} label="By appointment only" />
          {!byAppointment && (
            <div style={{ marginTop: 12 }}>
              {DAYS.map((d) => (
                <div key={d.value} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 3 }}>{d.label}</div>
                  {draftRanges[d.value]?.map((r) => (
                    <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <input type="time" className="styled-input" value={r.open_time} onChange={(e) => updateRange(d.value, r.key, 'open_time', e.target.value)} style={{ width: 110 }} />
                      <span style={{ color: '#888' }}>–</span>
                      <input type="time" className="styled-input" value={r.close_time} onChange={(e) => updateRange(d.value, r.key, 'close_time', e.target.value)} style={{ width: 110 }} />
                      <button type="button" onClick={() => removeRange(d.value, r.key)} title="Remove range" style={{ background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', display: 'flex' }}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addRange(d.value)}
                    style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', fontSize: 12, padding: 0, display: 'inline-flex', alignItems: 'center', gap: 3 }}
                  >
                    <Plus size={12} /> Add range
                  </button>
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea
              className="styled-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Exceptions or extra context, e.g. closed public holidays"
              rows={2}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ marginTop: 14 }}>
            <SaveButton type="button" onClick={handleSave} loading={saving} />
            <CancelButton onClick={cancelEdit} disabled={saving} style={{ marginLeft: 8 }} />
          </div>
        </div>
      )}
    </Accordion>
  );
}

export default OpeningHoursCard;
